import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  UPGRADE_SUCCESS_SESSION_KEY,
  UPGRADE_SUCCESS_SESSION_KEY_LEGACY,
} from "@/constants/browserStorageKeys";
import { auth } from "@/firebase/auth-config";
import { subscribeToAuthState } from "@/firebase/auth-state";
import { migrateSessionStorageKey } from "@/utils/migrateBrowserStorageKey";

interface SubscriptionContextType {
  isAnnualMember: boolean;
  isPremiumUser: boolean;
  loading: boolean;
  error: string | null;
  refreshSubscriptionStatus: (forceRefreshClaims?: boolean) => Promise<void>;
  setIsPremiumUser: (value: boolean) => void;
  setIsAnnualMember: (value: boolean) => void;
  subscriptionData: {
    id?: string;
    status?: string;
    currentPeriodEnd?: Date;
    planId?: string;
  } | null;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(
  undefined,
);

const UPGRADE_CONFIRM_DELAY_MS = 5000; // One confirmation refresh after this; don't overwrite to free before that

let subscriptionFirestorePromise: Promise<{
  db: any;
  doc: (typeof import("firebase/firestore"))["doc"];
  onSnapshot: (typeof import("firebase/firestore"))["onSnapshot"];
}> | null = null;

const loadSubscriptionFirestoreApis = () => {
  if (!subscriptionFirestorePromise) {
    subscriptionFirestorePromise = Promise.all([
      import("firebase/firestore"),
      import("@/firebase/config"),
    ]).then(([firestore, config]) => ({
      db: config.db,
      doc: firestore.doc,
      onSnapshot: firestore.onSnapshot,
    }));
  }

  return subscriptionFirestorePromise;
};

function isPendingUpgradeConfirm(): boolean {
  if (typeof window === "undefined") return false;
  migrateSessionStorageKey(
    UPGRADE_SUCCESS_SESSION_KEY,
    UPGRADE_SUCCESS_SESSION_KEY_LEGACY,
  );
  const raw = sessionStorage.getItem(UPGRADE_SUCCESS_SESSION_KEY);
  if (!raw) return false;
  const t = parseInt(raw, 10);
  return !Number.isNaN(t) && Date.now() - t < UPGRADE_CONFIRM_DELAY_MS;
}

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isAnnualMember, setIsAnnualMember] = useState(false);
  const [isPremiumUser, setIsPremiumUser] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionData, setSubscriptionData] = useState<any>(null);

  const refreshSubscriptionStatus = useCallback(
    async (forceRefreshClaims = false) => {
      try {
        setLoading(true);
        const user = auth?.currentUser ?? null;
        if (!user) {
          setIsAnnualMember(false);
          setIsPremiumUser(false);
          setSubscriptionData(null);
          return;
        }

        // Get user's custom claims (subscription status)
        const idTokenResult = await user.getIdTokenResult(
          forceRefreshClaims || isPendingUpgradeConfirm(),
        );
        const customClaims = idTokenResult.claims;

        // Check for subscription status in custom claims
        const hasActiveSubscription =
          customClaims.subscriptionStatus === "active";
        const hasLegacySubscription = customClaims.isPremiumUser === true;

        const isSubscribed = hasActiveSubscription || hasLegacySubscription;
        if (isSubscribed && typeof sessionStorage !== "undefined") {
          sessionStorage.removeItem(UPGRADE_SUCCESS_SESSION_KEY);
          sessionStorage.removeItem(UPGRADE_SUCCESS_SESSION_KEY_LEGACY);
        }
        const pendingConfirm = isPendingUpgradeConfirm();
        const showPremium = isSubscribed || pendingConfirm;

        setIsAnnualMember(showPremium);
        setIsPremiumUser(showPremium);
        setSubscriptionData({
          status: customClaims.subscriptionStatus || null,
          stripeSubscriptionId: customClaims.stripeSubscriptionId || null,
          updatedAt: customClaims.updatedAt || null,
        });
      } catch (err: any) {
        console.error("Error refreshing subscription status:", err);
        setError(err.message || "Failed to refresh subscription status");
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    const unsubscribeAuth = subscribeToAuthState(async (user) => {
      try {
        if (!user) {
          // No user logged in - default to free user
          setIsAnnualMember(false);
          setIsPremiumUser(false);
          setSubscriptionData(null);
          setLoading(false);
          return;
        }

        // User is logged in - fetch subscription status
        await refreshSubscriptionStatus(false);
      } catch (err: any) {
        console.error("Error fetching subscription status:", err);
        setError(err.message || "Failed to load subscription status");
        // Default to free user on error
        setIsAnnualMember(false);
        setIsPremiumUser(false);
        setSubscriptionData(null);
        setLoading(false);
      }
    });

    return unsubscribeAuth;
  }, [refreshSubscriptionStatus]);

  // Set up global event listener for subscription updates
  useEffect(() => {
    const handleSubscriptionUpdate = () => {
      refreshSubscriptionStatus(true);
    };

    window.addEventListener("subscriptionUpdated", handleSubscriptionUpdate);

    return () => {
      window.removeEventListener(
        "subscriptionUpdated",
        handleSubscriptionUpdate,
      );
    };
  }, [refreshSubscriptionStatus]);

  // Listen to Firestore subscription document for real-time updates
  useEffect(() => {
    const currentUser = auth?.currentUser;
    if (!currentUser) return () => {};

    let isCancelled = false;
    let unsubscribe: (() => void) | undefined;

    void loadSubscriptionFirestoreApis()
      .then(({ db, doc, onSnapshot }) => {
        if (isCancelled || !db || !auth?.currentUser) {
          return;
        }

        unsubscribe = onSnapshot(
          doc(db, "users", auth.currentUser.uid, "subscription", "current"),
          (docSnapshot) => {
            if (docSnapshot.exists()) {
              const data = docSnapshot.data();
              const isSubscribed = data.isPremiumUser === true;
              if (isSubscribed && typeof sessionStorage !== "undefined") {
                sessionStorage.removeItem(UPGRADE_SUCCESS_SESSION_KEY);
                sessionStorage.removeItem(UPGRADE_SUCCESS_SESSION_KEY_LEGACY);
              }
              setIsAnnualMember(isSubscribed);
              setIsPremiumUser(isSubscribed);
              setSubscriptionData({
                status: data.status || null,
                stripeSubscriptionId: data.stripeSubscriptionId || null,
                updatedAt: data.updatedAt || null,
              });
              setLoading(false);
            } else {
              if (!isPendingUpgradeConfirm()) {
                setIsAnnualMember(false);
                setIsPremiumUser(false);
                setSubscriptionData(null);
              }
              setLoading(false);
            }
          },
          (snapshotError) => {
            console.error(
              "Error listening to subscription document:",
              snapshotError,
            );
            setError(snapshotError.message);
            setLoading(false);
          },
        );
      })
      .catch((loadError: any) => {
        if (!isCancelled) {
          console.error("Error loading subscription listeners:", loadError);
          setError(loadError?.message || "Failed to load subscription status");
          setLoading(false);
        }
      });

    return () => {
      isCancelled = true;
      unsubscribe?.();
    };
  }, [auth?.currentUser?.uid]);

  const value: SubscriptionContextType = {
    isAnnualMember,
    isPremiumUser,
    loading,
    error,
    refreshSubscriptionStatus,
    setIsPremiumUser,
    setIsAnnualMember,
    subscriptionData,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error(
      "useSubscription must be used within a SubscriptionProvider",
    );
  }
  return context;
};

export const useOptionalSubscription = (): SubscriptionContextType | null => {
  return useContext(SubscriptionContext) ?? null;
};
